import { ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useCreateCartaoAmarelo } from '@/api/queries/cartao-amarelo-queries'
import { useKindList } from '@/api/queries/catalog-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { usePeople } from '@/api/queries/people-queries'
import { Form } from '@/components/forms/form'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardFooter,
	InspectionCardHeader,
	InspectionDateField,
	InspectionPhotoField,
} from '@/components/inspections'
import { CardContent } from '@/components/ui/card'
import { Field, FieldContent } from '@/components/ui/field'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { ROUTES } from '@/constants/routes'
import { useCartaoAmareloForm } from '@/hooks/forms/use-cartao-amarelo-form'
import { useFormErrorFocus } from '@/hooks/forms/use-form-error-focus'
import type { CartaoAmareloFormData } from '@/schemas/cartao-amarelo-schema'
import { useProfileStore } from '@/store'
import {
	getApiErrorMessage,
	isPayloadTooLargeApiError,
} from '@/utils/api-error'
import { convertHeicIfNeeded } from '@/utils/image-compression'

export const YellowCardPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const form = useCartaoAmareloForm()
	const {
		setFocus,
		formState: { errors },
	} = form

	const { handleError } = useFormErrorFocus(errors, setFocus)

	const kindId = form.watch('kindId')
	const image = form.watch('image')

	const { defaultBranchId, defaultContractId, defaultCustomerId } =
		useProfileStore()
	const { data: branchOptions } = useOrganization()

	// Context fields (persisted in zustand)
	const branchId = defaultBranchId
	const crId = defaultContractId

	const selectedBranch = branchOptions?.find((b) => b.id === branchId)
	const branchName = selectedBranch?.name || ''

	const { data: employeeData } = usePeople()

	const colors = INSPECTION_COLORS.sunnyYellow

	// Form fields
	const [photoPreview, setPhotoPreview] = useState<string | null>(null)

	const { mutateAsync, isPending } = useCreateCartaoAmarelo()

	const { data: kindData } = useKindList()
	const selectedKind = kindData?.items.find((k) => k.id === kindId)

	const handleCancel = () => {
		navigate(ROUTES.INSPECTIONS.YELLOW_CARD.INDEX)
	}

	const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = e.target.files?.[0]
		if (!picked) return
		const file = await convertHeicIfNeeded(picked)
		form.setValue('image', file, {
			shouldDirty: true,
			shouldValidate: true,
		})
		setPhotoPreview(URL.createObjectURL(file))
	}

	const handleRemovePhoto = () => {
		form.setValue('image', undefined, {
			shouldDirty: true,
			shouldValidate: true,
		})
		if (photoPreview) URL.revokeObjectURL(photoPreview)
		setPhotoPreview(null)
	}

	const handleSubmit = async (formData: CartaoAmareloFormData) => {
		if (kindId && (selectedKind?.risks ?? []).length === 0) {
			toast.error('A tipologia selecionada não possui riscos disponíveis')
			return
		}

		const selectedCr = selectedBranch?.crs?.find((c) => c.id === crId)

		if (
			!formData.date ||
			!formData.employeeId ||
			!formData.leaderId ||
			!formData.kindId ||
			!formData.riskId ||
			!formData.unsafeDescription ||
			!formData.safeDescription
		) {
			toast.error(t('inspections.yellowCard.validationError'))
			return
		}

		// require customer when CR has customers
		if ((selectedCr?.customers?.length ?? 0) > 0 && !defaultCustomerId) {
			toast.error(
				t('inspections.common.selectCustomerRequired') ||
					'Selecione um cliente',
			)
			return
		}

		try {
			await mutateAsync({
				crId,
				...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
				kindId: formData.kindId,
				riskId: formData.riskId,
				date: formData.date,
				employeeId: formData.employeeId,
				leaderId: formData.leaderId,
				unsafeDescription: formData.unsafeDescription,
				safeDescription: formData.safeDescription,
				image: image ?? undefined,
			})
			toast.success(t('inspections.yellowCard.saveSuccess'))
			navigate(ROUTES.INSPECTIONS.YELLOW_CARD.INDEX)
		} catch (error) {
			toast.error(
				isPayloadTooLargeApiError(error)
					? t('errors.payload_too_large')
					: getApiErrorMessage(error),
			)
		}
	}

	return (
		<InspectionCard colors={colors}>
			<InspectionCardHeader
				icon={<ShieldAlert className="size-5" />}
				title={t('inspections.types.yellowCard.title')}
				colors={colors}
				onBack={handleCancel}
				branch={branchName}
				branchId={branchId}
				branchList={branchOptions}
				crId={crId}
				crList={selectedBranch?.crs}
			/>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit, handleError)}
					className="flex flex-1 flex-col"
				>
					<CardContent className="flex flex-col gap-6 py-5">
						{/* ── Context fields ── */}
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
							<FormField
								name="date"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.common.date')}
										error={form.formState.errors.date?.message}
									>
										<InspectionDateField
											value={field.value ? new Date(field.value) : undefined}
											onChange={(date) =>
												field.onChange(date ? date.toISOString() : '')
											}
										/>
									</FormItem>
								)}
							/>

							<FormField
								name="employeeId"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.yellowCard.issuer')}
										error={form.formState.errors.employeeId?.message}
									>
										<FieldContent>
											<SearchableSelect
												ref={field.ref}
												value={field.value}
												nameField={field.name}
												onValueChange={field.onChange}
												options={
													employeeData?.map((p) => ({
														value: p.id,
														label: p.name,
													})) ?? []
												}
												placeholder={t('inspections.yellowCard.selectIssuer')}
												searchPlaceholder={t('common.search')}
											/>
										</FieldContent>
									</FormItem>
								)}
							/>

							<FormField
								name="leaderId"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.yellowCard.immediateLeader')}
										error={form.formState.errors.leaderId?.message}
									>
										<FieldContent fieldName={field.name}>
											<SearchableSelect
												ref={field.ref}
												nameField={field.name}
												value={field.value}
												onValueChange={field.onChange}
												options={
													employeeData?.map((p) => ({
														value: p.id,
														label: p.name,
													})) ?? []
												}
												placeholder={t(
													'inspections.yellowCard.selectImmediateLeader',
												)}
												searchPlaceholder={t('common.search')}
											/>
										</FieldContent>
									</FormItem>
								)}
							/>
						</div>

						<Separator className="bg-border/60" />

						<FormField
							name="kindId"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.yellowCard.typology')}
									error={form.formState.errors.kindId?.message}
								>
									<FieldContent fieldName={field.name}>
										<NativeSelect
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={(e) => {
												field.onChange(e)
												form.setValue('riskId', '')
											}}
											className="w-full min-w-40 truncate"
										>
											<NativeSelectOption value="" disabled>
												{t('inspections.yellowCard.selectTypology')}
											</NativeSelectOption>
											{kindData?.items.map((item) => (
												<NativeSelectOption
													key={item.id}
													value={item.id}
													disabled={(item.risks ?? []).length === 0}
												>
													{item.name}
												</NativeSelectOption>
											))}
										</NativeSelect>
									</FieldContent>
								</FormItem>
							)}
						/>

						{kindId && (
							<FormField
								name="riskId"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.yellowCard.risk')}
										error={form.formState.errors.riskId?.message}
									>
										<Field className="animate-in fade-in slide-in-from-top-2 duration-200">
											<FieldContent fieldName={field.name}>
												<NativeSelect
													name={field.name}
													ref={field.ref}
													value={field.value}
													onChange={field.onChange}
													className="w-full"
													disabled={(selectedKind?.risks ?? []).length === 0}
												>
													<NativeSelectOption value="" disabled>
														{t('inspections.yellowCard.selectRisk')}
													</NativeSelectOption>
													{(selectedKind?.risks ?? []).map((item) => (
														<NativeSelectOption key={item.id} value={item.id}>
															{item.name}
														</NativeSelectOption>
													))}
												</NativeSelect>
											</FieldContent>
										</Field>
									</FormItem>
								)}
							/>
						)}

						<FormField
							name="unsafeDescription"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.yellowCard.unsafeDescription')}
									error={form.formState.errors.unsafeDescription?.message}
								>
									<FieldContent fieldName={field.name}>
										<Textarea
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={field.onChange}
											placeholder={t(
												'inspections.yellowCard.unsafeDescriptionPlaceholder',
											)}
											rows={4}
											className="resize-y"
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="safeDescription"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.yellowCard.safetyImprovement')}
									error={form.formState.errors.safeDescription?.message}
								>
									<FieldContent fieldName={field.name}>
										<Textarea
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={field.onChange}
											placeholder={t(
												'inspections.yellowCard.safetyImprovementPlaceholder',
											)}
											rows={4}
											className="resize-y"
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="image"
							control={form.control}
							render={() => (
								<FormItem
									label={t('inspections.yellowCard.photo')}
									isRequired={false}
									error=""
								>
									<InspectionPhotoField
										attachLabel={t('inspections.yellowCard.attachPhoto')}
										altText={t('inspections.yellowCard.photoSelected')}
										preview={photoPreview}
										onFileChange={handlePhotoChange}
										onRemove={handleRemovePhoto}
										colors={colors}
									/>
								</FormItem>
							)}
						/>
					</CardContent>

					<InspectionCardFooter
						colors={colors}
						isSubmitting={isPending}
						onCancel={handleCancel}
						cancelLabel={t('inspections.yellowCard.cancel')}
						saveLabel={t('inspections.yellowCard.save')}
						savingLabel={t('inspections.yellowCard.saving')}
					/>
				</form>
			</Form>
		</InspectionCard>
	)
}
